import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import FreeLimitModal, { LimitType } from '../components/subscription/FreeLimitModal';
import { getFreemiumStatus, FreemiumStatusResponse } from '../services/tokenService';
import { useAuth } from './AuthContext';

interface FreeLimitState {
  isOpen: boolean;
  limitType: LimitType;
  currentUsage?: number;
  maxUsage?: number;
}

interface FreeLimitContextType {
  showLimitModal: (limitType: LimitType, currentUsage?: number, maxUsage?: number) => void;
  hideLimitModal: () => void;
  checkLimit: (limitType: LimitType, currentUsage: number, maxUsage: number) => boolean;
  freemiumStatus: FreemiumStatusResponse | null;
  refreshFreemiumStatus: () => Promise<void>;
}

const FreeLimitContext = createContext<FreeLimitContextType | undefined>(undefined);

interface FreeLimitProviderProps {
  children: ReactNode;
}

export const FreeLimitProvider: React.FC<FreeLimitProviderProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [state, setState] = useState<FreeLimitState>({
    isOpen: false,
    limitType: 'daily_courses',
  });
  const [freemiumStatus, setFreemiumStatus] = useState<FreemiumStatusResponse | null>(null);

  const refreshFreemiumStatus = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const status = await getFreemiumStatus();
      setFreemiumStatus(status);
    } catch {
      // Silently fail — status will be null
    }
  }, [isAuthenticated]);

  // Fetch freemium status on mount when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      refreshFreemiumStatus();
    } else {
      setFreemiumStatus(null);
    }
  }, [isAuthenticated, refreshFreemiumStatus]);

  const showLimitModal = useCallback((
    limitType: LimitType,
    currentUsage?: number,
    maxUsage?: number
  ) => {
    setState({
      isOpen: true,
      limitType,
      currentUsage,
      maxUsage,
    });
  }, []);

  const hideLimitModal = useCallback(() => {
    setState(prev => ({ ...prev, isOpen: false }));
  }, []);

  /**
   * Check if the user has reached their limit
   * Returns true if they can proceed, false if limited (and shows modal)
   */
  const checkLimit = useCallback((
    limitType: LimitType,
    currentUsage: number,
    maxUsage: number
  ): boolean => {
    if (currentUsage >= maxUsage) {
      showLimitModal(limitType, currentUsage, maxUsage);
      return false;
    }
    return true;
  }, [showLimitModal]);

  return (
    <FreeLimitContext.Provider value={{ showLimitModal, hideLimitModal, checkLimit, freemiumStatus, refreshFreemiumStatus }}>
      {children}
      <FreeLimitModal
        isOpen={state.isOpen}
        onClose={hideLimitModal}
        limitType={state.limitType}
        currentUsage={state.currentUsage}
        maxUsage={state.maxUsage}
      />
    </FreeLimitContext.Provider>
  );
};

export const useFreeLimit = (): FreeLimitContextType => {
  const context = useContext(FreeLimitContext);
  if (!context) {
    throw new Error('useFreeLimit must be used within a FreeLimitProvider');
  }
  return context;
};

// Export types for convenience
export type { LimitType };
